import { ErrorCategory, type DomainError } from '@core/domain/errors';
import { useSystemConfig } from '@core/hooks/useSystemConfig';
import { logger } from '@core/logging';
import { LogCategory } from '@soteria/logger/browser';
import { useEffect } from 'react';

import { HttpClient } from './HttpClient';

/**
 * Media service options
 */
export interface MediaServiceOptions {
  /** Base URL for the media service */
  baseUrl?: string;
  /** Request timeout in milliseconds */
  timeout?: number;
}

/**
 * Upload result from Media Service
 */
export interface MediaUploadResult {
  fileId: string;
  fileUrl: string;
  fileName: string;
  fileSize: number;
  uploadedAt: string;
}

/**
 * Upload request parameters
 */
export interface MediaUploadRequest {
  file: File;
  communityId: string;
  title?: string;
  description?: string;
}

/**
 * Service for interacting with the media service
 * Handles media operations, particularly audio files
 */
export class MediaService {
  private httpClient!: HttpClient;
  private baseUrl!: string;

  /**
   * Create a new MediaService instance
   * @param options Configuration options
   */
  constructor(options?: MediaServiceOptions) {
    if (options?.baseUrl) {
      this.initializeClient(options.baseUrl, options?.timeout);
    }
  }

  /**
   * Initialize the MediaService with a base URL
   * This method can be called after construction if a base URL wasn't provided initially
   * @param baseUrl The base URL for the media service
   * @param timeout Optional timeout in milliseconds
   */
  public initialize(baseUrl: string, timeout?: number): void {
    this.initializeClient(baseUrl, timeout);
  }

  /**
   * Initialize the HTTP client with the provided base URL
   * @param baseUrl The base URL for the media service
   * @param timeout Optional timeout in milliseconds
   */
  private initializeClient(baseUrl: string, timeout?: number): void {
    if (!baseUrl) {
      throw new Error('Media service URL is not configured');
    }

    this.baseUrl = baseUrl;
    this.httpClient = new HttpClient({
      baseUrl,
      timeout: timeout || 60000, // 60 seconds default for media operations
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });
  }

  /**
   * Parse error response from Media Service
   * @param errorText Raw error text from response
   * @param status HTTP status code
   * @param statusText HTTP status text
   * @returns Parsed error message
   */
  private parseErrorResponse(errorText: string, status: number, statusText: string): string {
    const errorMessage = `Media Service upload failed: ${status} ${statusText}`;

    try {
      const errorData = JSON.parse(errorText);

      if (errorData.message) {
        return errorData.message;
      } else if (errorData.error) {
        return errorData.error;
      }
    } catch {
      // If parsing fails, use the raw error text or default message
      return errorText || errorMessage;
    }

    return errorMessage;
  }

  /**
   * Fetch audio file from the media service
   * @param url Optional URL of the audio file to fetch. If not provided, will use the endpoint's default
   * @returns A Promise that resolves to a Blob containing the audio data
   */
  async getAudio(url?: string): Promise<Blob> {
    logger.debug('Fetching audio', {
      category: LogCategory.API,
      url,
    });

    try {
      // Ensure the HTTP client is initialized
      if (!this.httpClient) {
        throw new Error('MediaService not properly initialized: baseUrl is required');
      }

      return this.httpClient.post<Blob>('/api/v1/audio/file', { url });
    } catch (error) {
      logger.error('Error fetching audio file', {
        category: LogCategory.API,
        error,
        url,
      });

      throw error;
    }
  }

  /**
   * Upload a file to the Media Service
   * @param request Upload request parameters
   * @param accessToken Bearer token for authentication
   * @returns Promise that resolves to upload result
   */

  async uploadFile(request: MediaUploadRequest): Promise<MediaUploadResult> {
    logger.debug('Uploading file to media service', {
      category: LogCategory.API,
      fileName: request.file.name,
      fileSize: request.file.size,
      communityId: request.communityId,
    });

    try {
      // Ensure the HTTP client is initialized
      if (!this.httpClient) {
        throw new Error('MediaService not properly initialized: baseUrl is required');
      }

      const formData = new FormData();

      formData.append('audioFile', request.file);
      formData.append('CommunityId', request.communityId);
      formData.append('Title', request.title || request.file.name);

      if (request.description) {
        formData.append('Description', request.description);
      }

      // Use fetch directly for multipart form data upload
      const response = await fetch(`${this.baseUrl}/api/v1/audio/upload`, {
        method: 'POST',
        body: formData,
        credentials: 'include', // Include cookies for session-based auth
      });

      if (!response.ok) {
        const errorText = await response.text();
        const errorMessage = this.parseErrorResponse(errorText, response.status, response.statusText);

        throw new Error(errorMessage);
      }

      const result: MediaUploadResult = await response.json();

      logger.debug('File uploaded successfully', {
        category: LogCategory.API,
        fileId: result.fileId,
        fileUrl: result.fileUrl,
      });

      return result;
    } catch (error) {
      logger.error('Error uploading file to media service', {
        category: LogCategory.API,
        error,
        fileName: request.file.name,
        communityId: request.communityId,
      });

      throw error;
    }
  }
}

/**
 * Create a singleton instance of the MediaService
 * Note: This instance will need to be initialized with a baseUrl before use
 */
export const mediaService = new MediaService();

/**
 * Hook for using MediaService with error handling and dynamic initialization
 */
export function useMediaService() {
  const { mediaServiceUrl, loading: configLoading } = useSystemConfig();

  // Initialize MediaService when URL becomes available
  useEffect(() => {
    if (mediaServiceUrl && !configLoading) {
      try {
        mediaService.initialize(mediaServiceUrl);
        logger.debug('MediaService initialized from system config', {
          category: LogCategory.SYSTEM,
          mediaServiceUrl,
        });
      } catch (error) {
        logger.error('Failed to initialize MediaService from system config', {
          category: LogCategory.SYSTEM,
          error,
          mediaServiceUrl,
        });
      }
    }
  }, [mediaServiceUrl, configLoading]);

  const uploadFile = async (request: MediaUploadRequest): Promise<MediaUploadResult> => {
    try {
      if (!mediaServiceUrl) {
        throw new Error('Media Service URL not available from system config');
      }

      return await mediaService.uploadFile(request);
    } catch (error) {
      // Create a domain error that preserves the specific error message from Media Service
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload file';
      const domainError: DomainError = {
        code: 'MEDIA_UPLOAD_ERROR',
        message: errorMessage,
        userMessage: errorMessage, // Use the specific error message for user display
        category: ErrorCategory.SERVICE,
      };

      // Throw the domain error directly - let the calling hook handle transformErrors()
      throw domainError;
    }
  };

  const getAudio = async (url?: string): Promise<Blob> => {
    try {
      if (!mediaServiceUrl) {
        throw new Error('Media Service URL not available from system config');
      }

      return await mediaService.getAudio(url);
    } catch (error) {
      // Create a domain error that preserves the specific error message from Media Service
      const errorMessage = error instanceof Error ? error.message : 'Failed to get file';
      const domainError: DomainError = {
        code: 'MEDIA_GET_FILE_ERROR',
        message: errorMessage,
        userMessage: errorMessage, // Use the specific error message for user display
        category: ErrorCategory.SERVICE,
      };

      // Throw the domain error directly - let the calling hook handle transformErrors()
      throw domainError;
    }
  };

  return {
    uploadFile,
    getAudio,
    isConfigured: !!mediaServiceUrl && !configLoading,
    configLoading,
  };
}
